import React, { useEffect, useState } from "react";
import { api } from "../api";
import { money } from "./reportsFormat.mjs";
import { dateShort } from "./ui";

/*
 * WHO IS BEHIND A NUMBER.
 *
 * Click a funnel stage and this opens over the page listing the companies that
 * stage counted, newest event first. Click a row and that company's record
 * opens in a NEW TAB, so the report you were reading is still there when you
 * come back — the whole point of drilling into a number is to check it against
 * the actual records, and losing the report to do that defeats the exercise.
 *
 * The list comes from /reports/members, which reads the same event definitions
 * as the count. It is not a similar query written twice: a list that disagrees
 * with the headline it opened from is worse than no list.
 *
 * The record page is /contacts/:id. A company with no primary contact has
 * nothing to open, so its row renders unlinked and says why rather than
 * pointing somewhere plausible-looking.
 */

const STATUS_LABELS = {
  prospect: "Open",
  client: "Client",
  churned: "Churned",
  lost: "Lost",
  not_a_fit: "Not a fit",
  recycled: "Recycled",
  advisory: "Advisory",
};

const SOURCE_LABELS = {
  linkedin: "LinkedIn",
  referral: "Referral",
  organic: "Organic",
};

// What the event date MEANS for each stage, so the column header is honest.
const EVENT_COLUMN = {
  leads: "Entered",
  qualified: "Qualified",
  calls: "First call",
  priced: "Priced",
  opps: "Opportunity",
  won: "Won",
};

export default function ReportsMembers({ stage, scope, period, source, filterActive, onFilter, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    const query = new URLSearchParams({ stage, limit: "200" });
    /*
     * A `period` means the reader clicked one bar in the drill-down chart, so
     * the list is that month or week rather than the whole page scope. It wins
     * over the scope for exactly that reason: the number they clicked was the
     * period's, and a list covering twelve months would not contain it.
     */
    if (period) {
      query.set("from", period.from);
      query.set("to", period.to);
    } else if (scope?.custom) {
      query.set("from", scope.from_date);
      query.set("to", scope.to_date);
    } else if (scope?.days) {
      query.set("scope_days", String(scope.days));
    }
    if (source) query.set("source", source);
    setData(null);
    setError("");
    api(`/reports/members?${query}`)
      .then((next) => { if (live) setData(next); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [stage, scope, period, source]);

  // Escape closes it, like every other overlay in the app.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The period label ("Leads · Aug") when a bar was clicked, else the metric.
  const label = period?.label || data?.label || "Loading";

  return (
    <div className="crm-modal__backdrop" onClick={onClose}>
      <div className="crm-modal crm-members" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal__head">
          <h2>{label}</h2>
          <div className="crm-members__sub">
            {error ? (
              <span className="crm-hint--warn">{error}</span>
            ) : !data ? (
              "Loading…"
            ) : (
              <>
                {data.total} {data.total === 1 ? "company" : "companies"}
                {data.shown < data.total ? ` · showing the ${data.shown} most recent` : ""}
                {" · "}
                {/* to_date, not `to`: the latter is the exclusive midnight
                    after the window, so a list covering August printed
                    "Aug 1 – Sep 1". Same trap as the funnel caption. */}
                {dateShort(data.scope.from_date || data.scope.from)} –{" "}
                {dateShort(data.scope.to_date || data.scope.to)}
                {source ? ` · ${SOURCE_LABELS[source] || source} only` : ""}
              </>
            )}
          </div>
        </div>

        <div className="crm-modal__body crm-members__body">
          {data && !data.members.length && (
            <div className="crm-empty">Nothing reached this stage in this window.</div>
          )}

          {data && data.members.length > 0 && (
            <>
              <div className="crm-members__cols">
                <span className="crm-members__c-name">Company</span>
                <span className="crm-members__c-who">Contact</span>
                <span className="crm-members__c-src">Source</span>
                <span className="crm-members__c-status">Status</span>
                <span className="crm-members__c-date">{EVENT_COLUMN[stage] || "Event"}</span>
              </div>

              {data.members.map((m) => {
                const inner = (
                  <>
                    <span className="crm-members__c-name">
                      <b>{m.name || "(no name)"}</b>
                      {m.domain && <span className="crm-members__domain">{m.domain}</span>}
                    </span>
                    <span className="crm-members__c-who">
                      {m.contact_name || <span className="crm-members__none">no contact on record</span>}
                      {m.job_title && <span className="crm-members__domain">{m.job_title}</span>}
                    </span>
                    <span className="crm-members__c-src">
                      {m.source ? (
                        SOURCE_LABELS[m.source] || m.source
                      ) : (
                        <span className="crm-members__none">unattributed</span>
                      )}
                    </span>
                    <span className="crm-members__c-status">
                      {STATUS_LABELS[m.status] || m.status}
                      {m.monthly_value != null && (
                        <span className="crm-members__domain crm-num">
                          {money(m.monthly_value, m.currency)}/mo
                        </span>
                      )}
                    </span>
                    <span className="crm-members__c-date crm-num">{dateShort(m.event_at)}</span>
                  </>
                );

                return m.contact_id ? (
                  <a
                    key={m.company_id}
                    className="crm-members__row"
                    href={`/contacts/${m.contact_id}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Open this record in a new tab"
                  >
                    {inner}
                  </a>
                ) : (
                  <div
                    key={m.company_id}
                    className="crm-members__row is-unopenable"
                    title="No contact on this company, so there is no record page to open"
                  >
                    {inner}
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="crm-modal__foot">
          {/*
           * Clicking a stage used to filter the page directly. It now opens
           * this list, so the filter has to stay reachable from inside it.
           * Not offered for a single period: the page filter narrows by STAGE
           * over the whole scope, so pressing it here would silently widen the
           * window the reader just chose.
           */}
          {!period && (
            <button className="crm-btn crm-btn--dark" onClick={onFilter}>
              {filterActive ? "Clear the page filter" : "Filter the whole page to this stage"}
            </button>
          )}
          <button className="crm-btn crm-btn--ghost" onClick={onClose}>Close</button>
          <span className="crm-members__hint">Rows open in a new tab</span>
        </div>
      </div>
    </div>
  );
}
